import { addHours, format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Popover, IconButton } from '@mui/material'
import { useState, MouseEvent, useEffect } from 'react'

import Chip from '../common/chip'
import { Payment } from '../../types'
import { PaymentWithNoveltiesType } from '../../lib'
import { numberWithCommas } from '../../utils/common.utils'
import TicketIcon from '../../assets/icons/ticket-icon.svg'
import AccountIcon from '../../assets/icons/account-icon.svg'
import CalendarIcon from '../../assets/icons/calendar-icon.svg'
import MoreMenuIcon from '../../assets/icons/more-menu-icon.svg'
import MoneyGreenIcon from '../../assets/icons/money-icon-green.svg'
import ArrowRightIcon from '../../assets/icons/arrow-right-icon.svg'

const DocumentPaymentListItem = ({
  paymentData,
  topDistance,
  showDateData,
  showCustomerData,
  selectedMainFilter,
  paymentsResponseStatus,
  handleOpenPaymentDetailsModal
}: {
  paymentData: Payment
  topDistance?: number
  showDateData?: boolean
  showCustomerData?: boolean
  selectedMainFilter?: PaymentWithNoveltiesType
  paymentsResponseStatus?: Array<{ paymentId: string; status: string }>
  handleOpenPaymentDetailsModal: (selectedPaymentData: Partial<Payment>) => void
}) => {
  const location = useLocation()
  const { t } = useTranslation(['document-payments-details-management'])

  const [, /* responseStatus */ setResponseStatus] = useState('without-starting')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  useEffect(() => {
    if (selectedMainFilter === PaymentWithNoveltiesType.HARD_STOP) {
      const paymentResponseStatus = paymentsResponseStatus?.find((paymentResponseData) => String(paymentResponseData.paymentId) === String(paymentData._id))
      // console.log('selectedMainFilter', selectedMainFilter, 'paymentsResponseStatus', paymentResponseStatus)
      setResponseStatus(paymentResponseStatus ? paymentResponseStatus.status : 'without-starting')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainFilter, paymentsResponseStatus])

  // console.log('paymentsResponseStatus', paymentsResponseStatus)

  return (
    <>
      {location.pathname.indexOf('payments-with-alerts') > -1 ? (
        <a
          target="_self"
          rel="noopener noreferrer"
          href={`/payment-with-alerts-details/${String(paymentData._id)}/${paymentData.belongsTo}?filter=${selectedMainFilter}&n=${Math.round(Number(topDistance))}`}
        >
          <div className="customer-details-list-item-data-container">
            {/* <div className="customer-details-list-item-left-data"> */}
            {showDateData && (
              <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg">
                <div className="customer-details-list-item-icon">
                  <img
                    src={CalendarIcon}
                    alt="calendar-icon"
                  />
                </div>
                <div className="customer-details-list-item-text">{paymentData.createdAt ? format(addHours(new Date(paymentData.createdAt), 5), 'dd/MM/yyyy') : ''}</div>
              </div>
            )}
            {showCustomerData && (
              <div className="customer-company-table-column customer-company-table-width-45">
                <div className="customer-details-list-item-icon">
                  <img
                    src={AccountIcon}
                    alt="account-icon"
                  />
                </div>
                <div className="customer-details-list-item-text">
                  {paymentData.customerRegisteredFullName
                    ? paymentData.customerRegisteredFullName.toUpperCase()
                    : paymentData.customerData
                    ? paymentData.customerData?.registeredFullName?.toUpperCase()
                    : t('loadingText')}
                </div>
              </div>
            )}
            <div className="customer-company-table-column customer-company-table-width-15 customer-company-display-none-lg">
              <div className="customer-details-list-item-icon">
                <img
                  src={MoneyGreenIcon}
                  alt="money-icon"
                />
              </div>
              <div className="customer-details-list-item-text">
                {numberWithCommas(Number(paymentData.amount))} {paymentData.currency?.toUpperCase() ?? 'COP'}
              </div>
            </div>
            {/* </div>
        <div className="customer-details-list-item-right-data"> */}
            <div className="customer-company-table-column customer-company-table-width-20 customer-company-display-none-sm">
              <div className="customer-details-list-item-icon">
                <img
                  src={TicketIcon}
                  alt="ticket-icon"
                />
              </div>
              <div className="customer-details-list-item-text">{paymentData.referenceNumber}</div>
            </div>
            <div className="customer-company-table-column customer-company-table-width-10">
              <div className="customer-details-list-item-chip-container">
                <Chip
                  status={
                    selectedMainFilter === PaymentWithNoveltiesType.HARD_STOP
                      ? paymentsResponseStatus?.find((paymentResponseData) => String(paymentResponseData.paymentId) === String(paymentData._id))?.status || 'rejected'
                      : paymentData.status
                  }
                  label={
                    selectedMainFilter === PaymentWithNoveltiesType.HARD_STOP
                      ? t(`paymentStatus.${paymentsResponseStatus?.find((paymentResponseData) => String(paymentResponseData.paymentId) === String(paymentData._id))?.status || 'rejected'}`)
                      : paymentData?.status === 'reviewed'
                      ? t('paymentStatus.pending')
                      : paymentData?.status?.indexOf('error') !== -1
                      ? t('paymentStatus.error')
                      : paymentData?.status === 'paid'
                      ? t('paymentStatus.paid')
                      : paymentData?.status === 'processing'
                      ? t('paymentStatus.processing')
                      : paymentData?.status === 'rejected' || paymentData?.status === 'deleted'
                      ? t('paymentStatus.rejected')
                      : paymentData?.status === 'returned'
                      ? t('paymentStatus.returned')
                      : paymentData?.status === 'validated'
                      ? t('paymentStatus.validated')
                      : paymentData?.status === 'waiting-for-resolution'
                      ? t('paymentStatus.waitingForResolution')
                      : t('paymentStatus.pending')
                  }
                />
              </div>
              <div className="customer-details-list-item-arrow-right">
                {paymentData.paidAt && (
                  <IconButton
                    edge="end"
                    sx={{ color: '#107569' }}
                    onClick={(event: MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation()
                      setAnchorEl(event.currentTarget)
                    }}
                    aria-label="toggle password visibility"
                  >
                    <img
                      src={MoreMenuIcon}
                      alt="more-menu-icon"
                    />
                  </IconButton>
                )}
                <img
                  src={ArrowRightIcon}
                  alt="arrow-right-icon"
                />
              </div>
            </div>
            {/* </div> */}
          </div>
        </a>
      ) : (
        <div
          className="customer-details-list-item-data-container"
          onClick={() => handleOpenPaymentDetailsModal(paymentData)}
        >
          {/* <div className="customer-details-list-item-left-data"> */}
          {showDateData && (
            <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg">
              <div className="customer-details-list-item-icon">
                <img
                  src={CalendarIcon}
                  alt="calendar-icon"
                />
              </div>
              <div className="customer-details-list-item-text">{paymentData.createdAt ? format(addHours(new Date(paymentData.createdAt), 5), 'dd/MM/yyyy') : ''}</div>
            </div>
          )}
          {showCustomerData && (
            <div className="customer-company-table-column customer-company-table-width-45">
              <div className="customer-details-list-item-icon">
                <img
                  src={AccountIcon}
                  alt="account-icon"
                />
              </div>
              <div className="customer-details-list-item-text">
                {paymentData.customerRegisteredFullName ? paymentData.customerRegisteredFullName.toUpperCase() : paymentData.customerData ? paymentData.customerData?.registeredFullName?.toUpperCase() : t('loadingText')}
              </div>
            </div>
          )}
          <div className="customer-company-table-column customer-company-table-width-15 customer-company-display-none-lg">
            <div className="customer-details-list-item-icon">
              <img
                src={MoneyGreenIcon}
                alt="money-icon"
              />
            </div>
            <div className="customer-details-list-item-text">
              {numberWithCommas(Number(paymentData.amount))} {paymentData.currency?.toUpperCase() ?? 'COP'}
            </div>
          </div>
          {/* </div>
        <div className="customer-details-list-item-right-data"> */}
          <div className="customer-company-table-column customer-company-table-width-20 customer-company-display-none-sm">
            <div className="customer-details-list-item-icon">
              <img
                src={TicketIcon}
                alt="ticket-icon"
              />
            </div>
            <div className="customer-details-list-item-text">{paymentData.referenceNumber}</div>
          </div>
          <div className="customer-company-table-column customer-company-table-width-10">
            <div className="customer-details-list-item-chip-container">
              <Chip
                status={paymentData.status ?? 'reviewed'}
                label={
                  paymentData?.status === 'reviewed'
                    ? t('paymentStatus.pending')
                    : paymentData?.status?.indexOf('error') !== -1
                    ? t('paymentStatus.error')
                    : paymentData?.status === 'paid'
                    ? t('paymentStatus.paid')
                    : paymentData?.status === 'processing'
                    ? t('paymentStatus.processing')
                    : paymentData?.status === 'rejected' || paymentData?.status === 'deleted'
                    ? t('paymentStatus.rejected')
                    : paymentData?.status === 'returned'
                    ? t('paymentStatus.returned')
                    : paymentData?.status === 'validated'
                    ? t('paymentStatus.validated')
                    : paymentData?.status === 'waiting-for-resolution'
                    ? t('paymentStatus.waitingForResolution')
                    : t('paymentStatus.pending')
                }
              />
            </div>
            <div className="customer-details-list-item-arrow-right">
              {paymentData.paidAt && (
                <IconButton
                  edge="end"
                  sx={{ color: '#107569' }}
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    setAnchorEl(event.currentTarget)
                  }}
                  aria-label="toggle password visibility"
                >
                  <img
                    src={MoreMenuIcon}
                    alt="more-menu-icon"
                  />
                </IconButton>
              )}
              <img
                src={ArrowRightIcon}
                alt="arrow-right-icon"
              />
            </div>
          </div>
          {/* </div> */}
        </div>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <div className="documents-list-item-popover-container">
          <div className="documents-list-item-popover">
            <strong>{t('listItem.paidAt')}</strong>
            <div>{paymentData.paidAt ? format(paymentData.createdAt, 'dd/MM/yyyy | HH:mm:ss') : ''}</div>
          </div>
        </div>
      </Popover>
    </>
  )
}

export default DocumentPaymentListItem
